import React from 'react';
import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { createBlogSEO } from '@/utils/blogSeo';

export const CreateTokenGuide: React.FC = () => {
  const seo = createBlogSEO(
    "Create a Token on ETH & BSC - Complete Guide",
    "Step-by-step guide for creating ERC-20/BEP-20 tokens on Ethereum & BNB Chain. Includes deployment, verification, and security best practices.",
    [
      "create token",
      "Ethereum",
      "BNB Chain",
      "BSC",
      "ERC-20",
      "BEP-20",
      "smart contract",
      "token creation"
    ]
  );

  const howToStructuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Create a Token on Ethereum (ETH) & BNB Chain (BSC): Complete Guide 2025",
    "description": "Learn how to create your own ERC-20/BEP-20 token on Ethereum & BNB Chain. Step-by-step guide for token creation, deployment, and verification. Includes best practices and security tips. Updated for 2025.",
    "datePublished": "2024-12-15",
    "dateModified": "2024-12-15",
    "author": {
      "@type": "Person",
      "name": "Daniel T.",
      "url": "https://mdbapp.io/authors/dtdevmdbappio"
    },
    "step": [
      {
        "@type": "HowToStep",
        "name": "Preparation",
        "text": "Set up development environment and gather requirements"
      },
      {
        "@type": "HowToStep",
        "name": "Token Creation",
        "text": "Write and deploy smart contract for your token"
      },
      {
        "@type": "HowToStep",
        "name": "Verification",
        "text": "Verify and test your token on Ethereum or BNB Chain"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "Web3 Wallet",
        "description": "MetaMask, SafePal, or Trust Wallet"
      },
      {
        "@type": "HowToTool",
        "name": "MDB App Token Creator",
        "description": "Our user-friendly token creation interface"
      }
    ]
  };

  return (
    <>
      <SimpleSEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />

      <div className="max-w-4xl mx-auto space-y-8">
        <article className="prose prose-gray dark:prose-invert max-w-none">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#0a74ff] to-[#FFD700]">
              How to Create a Token on ETH & BSC Networks
            </h1>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-r from-[#0a74ff] to-[#FFD700] p-[2px]">
                <div className="w-full h-full rounded-full overflow-hidden">
                  <img 
                    src="/authors/dtdevmdbappio.jpg"
                    alt="Daniel T."
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://api.dicebear.com/7.x/initials/svg?seed=DT&backgroundColor=0a74ff';
                    }}
                  />
                </div>
              </div>
              <div>
                <p className="font-medium">Daniel T. (MDB Dev)</p>
                <p className="text-sm">Published on December 27, 2023</p>
              </div>
            </div>
          </header>

          <h1 className="text-4xl font-bold mb-4">{seo.title}</h1>
          <p className="text-muted-foreground">Published on December 15, 2024 • Last Updated December 15, 2024 • 10 min read</p>

          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mt-4">
            <p className="font-semibold text-gray-900 dark:text-gray-100">Quick Navigation:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2 text-gray-700 dark:text-gray-300">
              <li><a href="#prerequisites">Prerequisites & Setup</a></li>
              <li><a href="#token-creation">Token Creation Process</a></li>
              <li><a href="#deployment">Deployment Guide</a></li>
              <li><a href="#verification">Testing & Verification</a></li>
              <li><a href="#best-practices">Best Practices</a></li>
            </ul>
          </div>

          <h2 id="prerequisites" className="text-2xl font-semibold mt-8 mb-4">Prerequisites & Setup</h2>
          <p>
            Before creating your token on Ethereum or BNB Chain, ensure you have the following ready:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Development Environment:</strong> A code editor and Node.js installed
            </li>
            <li>
              <strong>Network Gas:</strong> Sufficient ETH/BNB in your wallet for deployment
            </li>
            <li>
              <strong>Web3 Wallet:</strong> MetaMask or similar wallet configured for your chosen network
            </li>
            <li>
              <strong>Token Requirements:</strong> Clear specifications for your token
            </li>
          </ul>

          <h2 id="token-creation" className="text-2xl font-semibold mt-8 mb-4">Token Creation Process</h2>
          <div className="space-y-4">
            <div className="bg-blue-500/10 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-500/20">
              <p className="font-semibold text-blue-600 dark:text-blue-400">💡 Pro Tip:</p>
              <p className="text-gray-700 dark:text-gray-300">Skip the technical complexity and create your token instantly using <a href="https://mdbapp.io/create" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline">MDBApp's Token Creator</a>. Our platform handles all the smart contract development and deployment for you, saving time and ensuring security.</p>
            </div>

            <h3 className="text-xl font-semibold">1. Define Token Parameters</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Token name and symbol</li>
              <li>Total supply and decimal places</li>
              <li>Token features (mintable, burnable, etc.)</li>
              <li>Distribution mechanism</li>
            </ul>

            <h3 className="text-xl font-semibold">2. Smart Contract Development</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Choose contract standard (ERC-20/BEP-20)</li>
              <li>Implement required functions</li>
              <li>Add custom features if needed</li>
              <li>Test contract locally</li>
            </ul>
          </div>

          <h2 id="deployment" className="text-2xl font-semibold mt-8 mb-4">Deployment Guide</h2>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">1. Network Setup</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Choose your network (Ethereum or BNB Chain)</li>
              <li>Connect to testnet first (Sepolia for ETH, BSC Testnet for BNB)</li>
              <li>Configure deployment settings</li>
              <li>Prepare deployment script</li>
            </ul>

            <h3 className="text-xl font-semibold">2. Contract Deployment</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Deploy to testnet for testing</li>
              <li>Verify contract functionality</li>
              <li>Deploy to mainnet when ready</li>
            </ul>
          </div>

          <h2 id="verification" className="text-2xl font-semibold mt-8 mb-4">Testing & Verification</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Contract Verification:</strong> Verify source code on Etherscan/BscScan
            </li>
            <li>
              <strong>Functionality Testing:</strong> Test all token features
            </li>
            <li>
              <strong>Security Audit:</strong> Consider professional audit services
            </li>
            <li>
              <strong>Community Testing:</strong> Engage community in testing
            </li>
          </ul>

          <h2 id="best-practices" className="text-2xl font-semibold mt-8 mb-4">Best Practices</h2>
          <div className="space-y-4">
            <div className="bg-emerald-500/10 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-500/20 mb-6">
              <p className="font-semibold text-emerald-600 dark:text-emerald-400">🚀 Ready to Launch?</p>
              <p className="text-gray-700 dark:text-gray-300">Create your token now with our secure, automated platform at <a href="https://mdbapp.io" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline">MDBApp.io</a>. Get features like:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700 dark:text-gray-300">
                <li>Instant token deployment</li>
                <li>Automated contract verification</li>
                <li>Built-in security features</li>
                <li>Professional token management dashboard</li>
              </ul>
            </div>
            <li>
              <strong>Security First:</strong> Implement security best practices
            </li>
            <li>
              <strong>Documentation:</strong> Maintain clear documentation
            </li>
            <li>
              <strong>Testing:</strong> Thorough testing before mainnet
            </li>
            <li>
              <strong>Transparency:</strong> Keep your community informed
            </li>
          </div>

          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mt-8">
            <p className="font-semibold text-gray-900 dark:text-gray-100">Pro Tip:</p>
            <p className="text-gray-700 dark:text-gray-300">
              Always test your token extensively on the testnet before deploying to mainnet. This saves costs and helps 
              identify potential issues early in the development process.
            </p>
          </div>
        </article>
      </div>
    </>
  );
};
